<template>
  <el-dialog
    :visible.sync="visible"
    :title="`ACRCloud Analysis - ${track && track.title ? track.title : ''}`"
    width="900px"
    custom-class="premium-modal acr-modal"
    @close="$emit('close')"
  >
    <div v-if="track && track.acr_cloud" class="acr-content">
      <!-- Music Matches Section -->
      <div v-if="track.acr_cloud.music && track.acr_cloud.music.length > 0" class="music-matches-section">
        <h3><i class="el-icon-headset"></i> Music Matches</h3>
        <div class="matches-grid">
          <div v-for="(match, index) in track.acr_cloud.music" :key="index" class="match-card">
            <div class="match-score">
              <div class="score-circle">
                <span>{{ match.score || 0 }}%</span>
              </div>
              <span class="match-label">Match Score</span>
            </div>
            <div class="match-details">
              <div v-if="match.artists && match.artists.length > 0" class="detail-row">
                <span class="label">Artists:</span>
                <span class="value">{{ match.artists.join(', ') }}</span>
              </div>
              <div v-if="match.label" class="detail-row">
                <span class="label">Label:</span>
                <span class="value">{{ match.label }}</span>
              </div>
              <div v-if="match.isrc" class="detail-row">
                <span class="label">ISRC:</span>
                <span class="value code">{{ match.isrc }}</span>
              </div>
              <div v-if="match.upc" class="detail-row">
                <span class="label">UPC:</span>
                <span class="value code">{{ match.upc }}</span>
              </div>
              <div v-if="match.release_date" class="detail-row">
                <span class="label">Release Date:</span>
                <span class="value">{{ match.release_date }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Metadata Section -->
      <div v-if="track.acr_cloud.metadata && track.acr_cloud.metadata.length > 0" class="metadata-section">
        <h3><i class="el-icon-link"></i> Streaming Links & Metadata</h3>
        <div v-for="(meta, index) in track.acr_cloud.metadata" :key="index" class="metadata-card">
          <div v-if="meta.image" class="meta-image">
            <img :src="meta.image" alt="Track artwork" />
          </div>
          <div class="streaming-links">
            <div v-if="meta.links">
              <div v-if="meta.links.spotify && meta.links.spotify.length > 0" class="link-group">
                <span class="platform-badge spotify">
                  <i class="el-icon-service"></i> Spotify
                </span>
                <a v-for="(link, idx) in meta.links.spotify" :key="idx" :href="link" target="_blank" class="stream-link">
                  Open in Spotify
                </a>
              </div>
              <div v-if="meta.links.applemusic && meta.links.applemusic.length > 0" class="link-group">
                <span class="platform-badge apple">
                  <i class="el-icon-platform-eleme"></i> Apple Music
                </span>
                <a v-for="(link, idx) in meta.links.applemusic" :key="idx" :href="link" target="_blank" class="stream-link">
                  Open in Apple Music
                </a>
              </div>
              <div v-if="meta.links.deezer && meta.links.deezer.length > 0" class="link-group">
                <span class="platform-badge deezer">
                  <i class="el-icon-headset"></i> Deezer
                </span>
                <a v-for="(link, idx) in meta.links.deezer" :key="idx" :href="link" target="_blank" class="stream-link">
                  Open in Deezer
                </a>
              </div>
              <div v-if="meta.links.youtube && meta.links.youtube.length > 0" class="link-group">
                <span class="platform-badge youtube">
                  <i class="el-icon-video-camera-solid"></i> YouTube
                </span>
                <a v-for="(link, idx) in meta.links.youtube.slice(0, 3)" :key="idx" :href="link" target="_blank" class="stream-link">
                  Watch on YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Analysis Section -->
      <div v-if="track.acr_cloud.analysis && track.acr_cloud.analysis.length > 0" class="analysis-section">
        <h3><i class="el-icon-warning-outline"></i> Analysis & Issues</h3>
        <div class="analysis-list">
          <div v-for="(item, index) in track.acr_cloud.analysis" :key="index" class="analysis-item">
            <i class="el-icon-warning" :class="getAnalysisClass(item)"></i>
            <span>{{ item }}</span>
          </div>
        </div>
      </div>

      <!-- Full Metadata Section -->
      <div v-if="track.acr_cloud.full_metadata && track.acr_cloud.full_metadata.length > 0" class="full-metadata-toggle">
        <el-button
          type="primary"
          size="medium"
          @click="showFullMetadata = !showFullMetadata"
        >
          <i :class="showFullMetadata ? 'el-icon-minus' : 'el-icon-plus'"></i>
          {{ showFullMetadata ? 'Hide' : 'Show' }} Full Metadata
        </el-button>
      </div>

      <div v-if="showFullMetadata && track.acr_cloud.full_metadata" class="full-metadata-section">
        <h3><i class="el-icon-document"></i> Complete Metadata Details</h3>
        <div v-for="(meta, index) in track.acr_cloud.full_metadata" :key="'full-' + index" class="metadata-item">
          <div class="meta-header">
            <div class="header-left">
              <span class="meta-index">{{ index + 1 }}</span>
              <span v-if="meta.name" class="meta-title">{{ meta.name }}</span>
            </div>
            <div v-if="meta.type" class="track-type">{{ meta.type }}</div>
          </div>

          <!-- Basic Info -->
          <div class="meta-group">
            <h4><i class="el-icon-info"></i> Basic Information</h4>
            <div class="info-grid">
              <div v-if="meta.isrc" class="info-item">
                <div class="info-icon">
                  <i class="el-icon-document-copy"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">ISRC</span>
                  <span class="info-value code">{{ meta.isrc }}</span>
                </div>
              </div>
              <div v-if="meta.disc_number" class="info-item">
                <div class="info-icon disc">
                  <i class="el-icon-coin"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">Disc</span>
                  <span class="info-value">{{ meta.disc_number }}</span>
                </div>
              </div>
              <div v-if="meta.track_number" class="info-item">
                <div class="info-icon track">
                  <i class="el-icon-sort"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">Track</span>
                  <span class="info-value">{{ meta.track_number }}</span>
                </div>
              </div>
              <div v-if="meta.duration_ms" class="info-item">
                <div class="info-icon duration">
                  <i class="el-icon-timer"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">Duration</span>
                  <span class="info-value">{{ formatDuration(meta.duration_ms) }}</span>
                </div>
              </div>
              <div v-if="meta.release_date" class="info-item">
                <div class="info-icon date">
                  <i class="el-icon-time"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">Released</span>
                  <span class="info-value">{{ meta.release_date }}</span>
                </div>
              </div>
              <div v-if="meta.genres && meta.genres.length > 0" class="info-item genres">
                <div class="info-icon genre">
                  <i class="el-icon-collection-tag"></i>
                </div>
                <div class="info-content">
                  <span class="info-label">Genres</span>
                  <div class="genre-tags">
                    <span v-for="genre in meta.genres" :key="genre" class="genre-tag">{{ genre }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Artists -->
          <div v-if="meta.artists && meta.artists.length > 0" class="meta-group">
            <h4>Artists</h4>
            <div class="artists-list">
              <span v-for="(artist, idx) in meta.artists" :key="idx" class="artist-name">
                {{ artist.name || 'Unknown Artist' }}
              </span>
            </div>
          </div>

          <!-- Album Info -->
          <div v-if="meta.album" class="meta-group">
            <h4>Album Information</h4>
            <div class="album-info">
              <div v-if="meta.album.covers && meta.album.covers.medium" class="album-cover">
                <img :src="meta.album.covers.medium" alt="Album cover" />
              </div>
              <div class="album-details">
                <div v-if="meta.album.upc" class="meta-field">
                  <span class="field-label">UPC:</span>
                  <span class="field-value code">{{ meta.album.upc }}</span>
                </div>
                <div v-if="meta.album.label" class="meta-field">
                  <span class="field-label">Label:</span>
                  <span class="field-value">{{ meta.album.label }}</span>
                </div>
                <div v-if="meta.album.track_count" class="meta-field">
                  <span class="field-label">Tracks:</span>
                  <span class="field-value">{{ meta.album.track_count }}</span>
                </div>
                <div v-if="meta.album.release_date" class="meta-field">
                  <span class="field-label">Released:</span>
                  <span class="field-value">{{ meta.album.release_date }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- External Metadata -->
          <div v-if="meta.external_metadata" class="meta-group">
            <h4>External Platforms</h4>
            <div class="external-platforms">
              <!-- Spotify -->
              <div v-if="meta.external_metadata.spotify && meta.external_metadata.spotify.length > 0" class="platform-section">
                <span class="platform-badge spotify">
                  <i class="el-icon-service"></i> Spotify ({{ meta.external_metadata.spotify.length }})
                </span>
                <div class="platform-items">
                  <a v-for="(item, idx) in meta.external_metadata.spotify.slice(0, 3)"
                     :key="'spotify-' + idx"
                     :href="item.link"
                     target="_blank"
                     class="external-link"
                     v-if="item.link">
                    <i class="el-icon-link"></i> Track {{ idx + 1 }}
                  </a>
                </div>
              </div>

              <!-- Apple Music -->
              <div v-if="meta.external_metadata.applemusic && meta.external_metadata.applemusic.length > 0" class="platform-section">
                <span class="platform-badge apple">
                  <i class="el-icon-platform-eleme"></i> Apple Music ({{ meta.external_metadata.applemusic.length }})
                </span>
                <div class="platform-items">
                  <a v-for="(item, idx) in meta.external_metadata.applemusic.slice(0, 3)"
                     :key="'apple-' + idx"
                     :href="item.link"
                     target="_blank"
                     class="external-link"
                     v-if="item.link">
                    <i class="el-icon-link"></i> Track {{ idx + 1 }}
                  </a>
                </div>
              </div>

              <!-- Deezer -->
              <div v-if="meta.external_metadata.deezer && meta.external_metadata.deezer.length > 0" class="platform-section">
                <span class="platform-badge deezer">
                  <i class="el-icon-headset"></i> Deezer ({{ meta.external_metadata.deezer.length }})
                </span>
                <div class="platform-items">
                  <a v-for="(item, idx) in meta.external_metadata.deezer.slice(0, 3)"
                     :key="'deezer-' + idx"
                     :href="item.link"
                     target="_blank"
                     class="external-link"
                     v-if="item.link">
                    <i class="el-icon-link"></i> Track {{ idx + 1 }}
                  </a>
                </div>
              </div>

              <!-- YouTube -->
              <div v-if="meta.external_metadata.youtube && meta.external_metadata.youtube.length > 0" class="platform-section">
                <span class="platform-badge youtube">
                  <i class="el-icon-video-camera-solid"></i> YouTube ({{ meta.external_metadata.youtube.length }})
                </span>
                <div class="platform-items">
                  <a v-for="(item, idx) in meta.external_metadata.youtube.slice(0, 3)"
                     :key="'youtube-' + idx"
                     :href="item.link"
                     target="_blank"
                     class="external-link"
                     v-if="item.link">
                    <i class="el-icon-link"></i> Video {{ idx + 1 }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Works/Publishing Info -->
          <div v-if="meta.works && meta.works.length > 0" class="meta-group">
            <h4>Publishing Information</h4>
            <div v-for="(work, widx) in meta.works" :key="'work-' + widx" class="work-item">
              <div class="work-header">
                <span v-if="work.name" class="work-name">{{ work.name }}</span>
                <span v-if="work.iswc" class="work-iswc">ISWC: {{ work.iswc }}</span>
              </div>
              <div v-if="work.contributors && work.contributors.length > 0" class="contributors">
                <div v-for="(contributor, cidx) in work.contributors" :key="'contrib-' + cidx" class="contributor">
                  <span class="contributor-name">{{ contributor.name || 'Unknown' }}</span>
                  <span v-if="contributor.roles && contributor.roles.length > 0" class="contributor-roles">
                    ({{ contributor.roles.join(', ') }})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Data Message -->
      <div v-if="!hasACRData" class="no-data">
        <i class="el-icon-info"></i>
        <p>No ACRCloud data available for this track</p>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'ACRCloudModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    track: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      showFullMetadata: false
    }
  },

  computed: {
    hasACRData() {
      if (!this.track || !this.track.acr_cloud) return false
      return !!(this.track.acr_cloud.music || this.track.acr_cloud.metadata || this.track.acr_cloud.analysis)
    }
  },

  methods: {
    getAnalysisClass(item) {
      if (!item || typeof item !== 'string') return ''
      const lowerItem = item.toLowerCase()
      if (lowerItem.includes('error') || lowerItem.includes('copyright')) {
        return 'error'
      }
      if (lowerItem.includes('not match')) {
        return 'warning'
      }
      return ''
    },
    formatDuration(ms) {
      if (!ms) return 'N/A'
      const seconds = Math.floor(ms / 1000)
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return minutes + ':' + (remainingSeconds < 10 ? '0' : '') + remainingSeconds
    }
  },

  watch: {
    visible(val) {
      if (!val) {
        this.showFullMetadata = false
        this.$emit('update:visible', false)
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Premium Modal Styles
.premium-modal {
  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #e55279 0%, #9b6dbb 100%);
    padding: 20px;

    .el-dialog__title {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: #f0f0f0;
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 30px;
  }
}

// ACRCloud Modal Styles
.acr-modal {
  ::v-deep .acr-content {
    .music-matches-section {
      margin-bottom: 30px;

      h3 {
        color: #495057;
        font-size: 16px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #4facfe;
        }
      }

      .matches-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
        gap: 20px;

        .match-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border: 1px solid #e9ecef;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          gap: 20px;
          transition: all 0.3s ease;

          &:hover {
            box-shadow: 0 8px 24px rgba(79, 172, 254, 0.1);
            transform: translateY(-2px);
          }

          .match-score {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;

            .score-circle {
              width: 70px;
              height: 70px;
              border-radius: 50%;
              background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 18px;
              box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
            }

            .match-label {
              font-size: 11px;
              color: #868e96;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
          }

          .match-details {
            flex: 1;

            .detail-row {
              display: flex;
              margin-bottom: 8px;
              font-size: 13px;

              .label {
                color: #868e96;
                width: 100px;
                flex-shrink: 0;
              }

              .value {
                color: #495057;
                font-weight: 500;

                &.code {
                  font-family: monospace;
                  background: #f1f3f5;
                  padding: 2px 6px;
                  border-radius: 4px;
                }
              }
            }
          }
        }
      }
    }

    .metadata-section {
      margin-bottom: 30px;

      h3 {
        color: #495057;
        font-size: 16px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #00f2fe;
        }
      }

      .metadata-card {
        background: #f8f9fa;
        border-radius: 12px;
        padding: 20px;
        display: flex;
        gap: 20px;

        .meta-image {
          img {
            width: 100px;
            height: 100px;
            object-fit: cover;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
        }

        .streaming-links {
          flex: 1;

          .link-group {
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;

            .platform-badge {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              color: white;
              display: inline-flex;
              align-items: center;
              gap: 5px;

              &.spotify {
                background: linear-gradient(135deg, #1db954 0%, #1ed760 100%);
              }

              &.apple {
                background: linear-gradient(135deg, #fc3c44 0%, #fa233b 100%);
              }

              &.deezer {
                background: linear-gradient(135deg, #ff6d00 0%, #ff9100 100%);
              }

              &.youtube {
                background: linear-gradient(135deg, #ff0000 0%, #cc0000 100%);
              }
            }

            .stream-link {
              color: #4facfe;
              text-decoration: none;
              font-size: 13px;
              padding: 4px 8px;
              border: 1px solid #4facfe;
              border-radius: 16px;
              transition: all 0.3s ease;

              &:hover {
                background: #4facfe;
                color: white;
              }
            }
          }
        }
      }
    }

    .analysis-section {
      background: linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%);
      border-radius: 12px;
      padding: 20px;
      border: 1px solid #ffb3b3;

      h3 {
        color: #c92a2a;
        font-size: 16px;
        margin-bottom: 15px;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #fa5252;
        }
      }

      .analysis-list {
        .analysis-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          padding: 10px;
          background: white;
          border-radius: 8px;
          font-size: 14px;
          color: #495057;

          i {
            flex-shrink: 0;

            &.error {
              color: #fa5252;
            }

            &.warning {
              color: #fd7e14;
            }
          }
        }
      }
    }

    .no-data {
      text-align: center;
      padding: 40px;
      color: #868e96;

      i {
        font-size: 48px;
        color: #dee2e6;
        margin-bottom: 15px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }

  .full-metadata-toggle {
      text-align: center;
      margin: 30px 0;
      padding-top: 20px;
      border-top: 1px solid #e9ecef;

      .el-button {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        font-weight: 600;
        padding: 12px 24px;

        &:hover {
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
        }
      }
    }

    .full-metadata-section {
      margin-top: 30px;
      background: linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%);
      border-radius: 12px;
      padding: 25px;
      border: 1px solid #e1e8ed;

      h3 {
        color: #495057;
        font-size: 18px;
        margin-bottom: 25px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding-bottom: 15px;
        border-bottom: 2px solid #e9ecef;

        i {
          color: #667eea;
        }
      }

      .metadata-item {
        background: white;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        border: 1px solid #e9ecef;

        .meta-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f1f3f5;

          .header-left {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .meta-index {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
          }

          .meta-title {
            font-size: 18px;
            font-weight: 600;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .track-type {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        }

        .meta-group {
          margin-bottom: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          h4 {
            color: #495057;
            font-size: 14px;
            margin-bottom: 20px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;

            i {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 15px;

            .info-item {
              background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
              border: 1px solid #e9ecef;
              border-radius: 10px;
              padding: 15px;
              display: flex;
              align-items: center;
              gap: 12px;
              transition: all 0.3s ease;

              &:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
                border-color: #667eea;
              }

              &.genres {
                grid-column: 1 / -1;
              }

              .info-icon {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                flex-shrink: 0;

                &.disc {
                  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
                }

                &.track {
                  background: linear-gradient(135deg, #30cfd0 0%, #330867 100%);
                }

                &.duration {
                  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
                }

                &.date {
                  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
                }

                &.genre {
                  background: linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%);
                }

                i {
                  color: white;
                  font-size: 18px;
                }
              }

              .info-content {
                flex: 1;

                .info-label {
                  display: block;
                  color: #868e96;
                  font-size: 11px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  margin-bottom: 4px;
                }

                .info-value {
                  display: block;
                  color: #495057;
                  font-size: 14px;
                  font-weight: 600;

                  &.code {
                    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                  }
                }

                .genre-tags {
                  display: flex;
                  gap: 6px;
                  flex-wrap: wrap;
                  margin-top: 4px;

                  .genre-tag {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 5px 12px;
                    border-radius: 15px;
                    font-size: 11px;
                    font-weight: 500;
                    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
                  }
                }
              }
            }
          }

          .artists-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;

            .artist-name {
              background: linear-gradient(135deg, #fce4ec 0%, #f3e5f5 100%);
              color: #ad1457;
              padding: 6px 14px;
              border-radius: 20px;
              font-size: 13px;
              font-weight: 500;
              border: 1px solid #f8bbd0;
            }
          }

          .album-info {
            display: flex;
            gap: 15px;

            .album-cover {
              flex-shrink: 0;

              img {
                width: 80px;
                height: 80px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                object-fit: cover;
              }
            }

            .album-details {
              flex: 1;
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;

              .meta-field {
                background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                border: 1px solid #e9ecef;
                border-radius: 10px;
                padding: 15px;
                display: flex;
                flex-direction: column;
                gap: 8px;
                transition: all 0.3s ease;

                &:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
                  border-color: #667eea;
                }

                .field-label {
                  color: #868e96;
                  font-size: 11px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  font-weight: 600;
                  margin-bottom: 4px;
                  display: flex;
                  align-items: center;
                  gap: 6px;

                  &::before {
                    content: '';
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                  }
                }

                .field-value {
                  color: #495057;
                  font-size: 14px;
                  font-weight: 600;

                  &.code {
                    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
                    background: linear-gradient(135deg, #f1f3f5 0%, #ffffff 100%);
                    padding: 8px 12px;
                    border-radius: 6px;
                    border: 1px solid #e9ecef;
                    color: #667eea;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                  }
                }
              }
            }
          }

          .external-platforms {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;

            .platform-section {
              background: #f8f9fa;
              border-radius: 8px;
              padding: 12px;

              .platform-badge {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 6px 12px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: 600;
                color: white;
                margin-bottom: 10px;

                &.spotify {
                  background: linear-gradient(135deg, #1db954 0%, #1ed760 100%);
                }

                &.apple {
                  background: linear-gradient(135deg, #fc3c44 0%, #fa233b 100%);
                }

                &.deezer {
                  background: linear-gradient(135deg, #ff6d00 0%, #ff9100 100%);
                }

                &.youtube {
                  background: linear-gradient(135deg, #ff0000 0%, #cc0000 100%);
                }
              }

              .platform-items {
                display: flex;
                flex-direction: column;
                gap: 6px;

                .external-link {
                  color: #4facfe;
                  text-decoration: none;
                  font-size: 12px;
                  padding: 4px 8px;
                  background: white;
                  border-radius: 6px;
                  border: 1px solid #e9ecef;
                  display: inline-flex;
                  align-items: center;
                  gap: 5px;
                  transition: all 0.3s ease;

                  &:hover {
                    background: #4facfe;
                    color: white;
                    border-color: #4facfe;
                  }
                }
              }
            }
          }

          .work-item {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 10px;

            &:last-child {
              margin-bottom: 0;
            }

            .work-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 10px;
              padding-bottom: 8px;
              border-bottom: 1px solid #e9ecef;

              .work-name {
                font-weight: 600;
                color: #495057;
                font-size: 13px;
              }

              .work-iswc {
                font-family: 'Courier New', monospace;
                font-size: 11px;
                color: #868e96;
                background: white;
                padding: 2px 6px;
                border-radius: 4px;
              }
            }

            .contributors {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;

              .contributor {
                background: white;
                padding: 4px 8px;
                border-radius: 6px;
                font-size: 11px;
                border: 1px solid #e9ecef;

                .contributor-name {
                  color: #495057;
                  font-weight: 500;
                }

                .contributor-roles {
                  color: #adb5bd;
                  margin-left: 4px;
                }
              }
            }
          }
        }
      }
    }
}
</style>